import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { SSO_ENABLED, useAuth } from '../auth/AuthProvider';
import { USING_EMULATORS } from '../firebase';
import { normalizeAppName } from '../lib/generalSettings';
import { workflowEnabledFor } from '../lib/workflowAccess';
import { useDoc } from '../lib/useFirestore';
import { useWalkthroughState } from '../lib/useWalkthroughState';
import { useTheme } from '../lib/theme';
import VersionPill from './VersionPill';
import {
  IconAdmin,
  IconHelp,
  IconApprovals,
  IconMyMatters,
  IconNewMatter,
  IconNumbering,
  IconRouting,
  IconTemplates,
} from './NavIcons';

/**
 * The suite shell: a fixed navy icon rail, a white header beside it, and the page below.
 *
 * The rail is the frame the three AALRR apps share, so it stays navy in both light and dark mode —
 * it is not a surface that flips with the theme, it is the product's edge. Everything to the right
 * of it keeps its dark variants.
 *
 * Deviation from the handoff, deliberately: it lists the intake rail as My matters · New matter ·
 * Templates · Numbering · Admin, which omits My Approvals and Routing. Those are live routes that
 * some people reach the app exclusively to use, and the same handoff says not to change routes —
 * so they are in the rail, in place, still behind the same conditions that governed them before.
 * Dropping a person's only route to their approval queue is not a styling change.
 */

const RAIL_ITEM =
  'flex flex-col items-center gap-1.5 px-1 py-3 text-[11.5px] leading-tight text-center transition-colors';

function railClass({ isActive }) {
  return `${RAIL_ITEM} relative ${
    isActive
      ? 'bg-white/[.09] font-medium text-white'
      : 'text-rail-label hover:bg-white/[.06] hover:text-white'
  }`;
}

/** The 3px full-height accent bar that marks the active item. */
function ActiveBar({ isActive }) {
  return isActive ? (
    <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-accent-500" />
  ) : null;
}

function RailLink({ to, end, label, Icon }) {
  return (
    // `data-rail` is the anchor the practice run highlights. Keyed on the route rather than the
    // label, because the label is what a firm would rename.
    <NavLink to={to} end={end} className={railClass} data-rail={to}>
      {({ isActive }) => (
        <>
          <ActiveBar isActive={isActive} />
          <Icon />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function RoleDisclosure({ displayName, roles }) {
  const [open, setOpen] = useState(false);

  if (roles.length === 0) {
    return <span className="text-sm text-slate-600 dark:text-slate-300">{displayName}</span>;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((isOpen) => !isOpen)}
        aria-expanded={open}
        aria-controls="user-roles"
        className="inline-flex items-center gap-1 rounded text-sm text-slate-600 dark:text-slate-300 hover:text-firm-700 dark:hover:text-firm-100 focus:outline-none focus:ring-2 focus:ring-firm-500 dark:focus:ring-firm-100 focus:ring-offset-1 dark:focus:ring-offset-slate-900"
        title="Show assigned roles"
      >
        <span>{displayName}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="m3 6 5 5 5-5" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setOpen(false)} />
          <div
            id="user-roles"
            role="status"
            className="absolute left-0 z-20 mt-1 w-max max-w-64 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-700 dark:text-slate-200 shadow-lg"
          >
            <span className="font-medium text-slate-500 dark:text-slate-400">Roles:</span> {roles.join(', ')}
          </div>
        </>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="rounded border border-slate-300 dark:border-slate-600 p-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-firm-500 dark:focus:ring-firm-100 focus:ring-offset-1 dark:focus:ring-offset-slate-900"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="10" cy="10" r="3.25" />
          <path d="M10 1.75v2M10 16.25v2M18.25 10h-2M3.75 10h-2M15.83 4.17l-1.42 1.42M5.59 14.41l-1.42 1.42M15.83 15.83l-1.42-1.42M5.59 5.59 4.17 4.17" />
        </svg>
      ) : (
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M16.75 12.25A6.75 6.75 0 0 1 7.75 3.25a6.75 6.75 0 1 0 9 9Z" />
        </svg>
      )}
    </button>
  );
}

export default function Shell() {
  const { user, displayName, roles, isAdmin, isIntake, isWorkflowViewer, isReminderSender, signOut } = useAuth();

  // Admin-only heads-up so nobody forgets test mode is on (see AdminTestMode.jsx /
  // functions/src/mail/testMode.js) — every outbound email is being redirected while
  // this is true. Only fetched for admins since only admins can act on it anyway.
  const { data: testMode } = useDoc(isAdmin ? 'settings/testMode' : null);
  const { data: general } = useDoc('settings/general');
  // A workflow pilot user must be able to reach their approval queue while the firm-wide
  // switch is off — otherwise the workflow can be exercised but never reviewed. Same
  // predicate the server uses to decide whether a submission builds a chain.
  const workflowEnabled = workflowEnabledFor(general, user?.uid);
  const appName = normalizeAppName(general?.appName);

  /**
   * The walkthrough, once per person.
   *
   * Mounted in the Shell rather than on one page, because somebody new does not reliably
   * arrive at the intake form first — they follow a link from an email, or land on My matters
   * and wonder what any of it is. `seen` is null until the answer is known, so nothing ever
   * appears and then vanishes.
   */
  const { seen: walkthroughSeen, markSeen: markWalkthroughSeen } = useWalkthroughState();
  const [walkthroughDismissed, setWalkthroughDismissed] = useState(false);

  return (
    <div className="min-h-full">
      {/*
        An INVITATION, not an interruption. This used to open a dialog over whatever somebody was
        doing; the practice run now happens on the real form, which is a place to go rather than a
        thing to dismiss — so a bar offers it and gets out of the way. Declining counts as seen,
        because somebody who said no has made their decision.
      */}
      {walkthroughSeen === false && !walkthroughDismissed && (
        <div className="fixed inset-x-0 top-0 z-40 flex items-center gap-3 bg-firm-700 px-4 py-2 text-sm text-white dark:bg-firm-800">
          <span>New here? Take a practice run at the matter form — nothing you do in it is saved.</span>
          <Link
            to="/matters/practice"
            onClick={() => markWalkthroughSeen()}
            className="rounded bg-white/15 px-3 py-1 text-xs font-medium hover:bg-white/25"
          >
            Start the practice run
          </Link>
          <button
            type="button"
            onClick={() => {
              setWalkthroughDismissed(true);
              markWalkthroughSeen();
            }}
            className="ml-auto text-xs text-firm-200 underline hover:text-white"
          >
            No thanks
          </button>
        </div>
      )}
      {/* The rail. Fixed width, navy in both themes — it is the product's frame, not a surface. */}
      <nav
        aria-label="Main"
        className="fixed inset-y-0 left-0 z-30 flex w-[106px] flex-col overflow-y-auto bg-rail"
      >
        <div className="flex h-[58px] shrink-0 items-center justify-center border-b border-white/[.09]">
          {/* No plate behind the mark — the rail is already the frame it needs. */}
          <img src="/intake-mark.svg" alt="" aria-hidden="true" className="h-[36px] w-[36px]" />
        </div>

        <div className="flex flex-col py-2">
          {/* Starting a matter is what most people open this app to do, so it leads. */}
          <RailLink to="/matters/new" label="New matter" Icon={IconNewMatter} />
          <RailLink to="/matters" end label="My matters" Icon={IconMyMatters} />
          {workflowEnabled && <RailLink to="/queue" label="Approvals" Icon={IconApprovals} />}
          <RailLink to="/templates" label="Templates" Icon={IconTemplates} />
          <RailLink to="/help" label="Help" Icon={IconHelp} />
          {isIntake && <RailLink to="/numbering" label="Numbering" Icon={IconNumbering} />}
          {workflowEnabled && (isAdmin || isWorkflowViewer || isReminderSender) && (
            <RailLink to="/workflow" label="Routing" Icon={IconRouting} />
          )}

          {isAdmin && (
            <>
              <span aria-hidden="true" className="mx-[18px] my-3.5 border-t border-white/10" />
              <RailLink to="/admin" label="Admin" Icon={IconAdmin} />
            </>
          )}
        </div>
      </nav>

      {/* Everything to the right of the rail keeps its light/dark surfaces. */}
      <div className="ml-[106px] flex min-w-0 flex-1 flex-col">
        {!SSO_ENABLED && (
          <div className="border-b border-amber-200 bg-amber-100 px-8 py-2 text-center text-[13px] text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            Development build — temporary sign-in, Microsoft Graph mail is mocked, no production
            Firebase project is connected.
          </div>
        )}

        {isAdmin && testMode?.enabled && (
          <div className="border-b border-amber-200 bg-amber-100 px-8 py-2 text-center text-[13px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            Test mode is on — all email is being redirected to{' '}
            {testMode.testEmail || '(no address set)'}.
          </div>
        )}

        <header className="flex h-[58px] shrink-0 items-center gap-4 border-b border-slate-200 bg-white px-[30px] dark:border-slate-700 dark:bg-slate-900">
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[19px] font-semibold text-firm-700 dark:text-firm-100">
              {appName}
            </span>
            <RoleDisclosure displayName={displayName} roles={roles} />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-3 text-sm">
            <VersionPill />
            <ThemeToggle />
            <button
              type="button"
              onClick={signOut}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-[13px] text-firm-700 hover:border-accent-500 dark:border-slate-600 dark:text-slate-100"
            >
              Sign out
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-[30px] py-7">
          <Outlet />
        </main>

        {USING_EMULATORS && (
          <footer className="border-t border-slate-200 bg-white px-[30px] py-2 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900">
            Connected to the Firebase Local Emulator Suite ·{' '}
            <a href="http://localhost:4000" className="text-firm-500 hover:underline">
              Emulator UI
            </a>
          </footer>
        )}
      </div>
    </div>
  );
}
